import React, {useMemo, useCallback, useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import './LoginPopUp.scss';
import PhoneStage from './Stages/Phone/PhoneStage';
import OtpStage from './Stages/Otp/OtpStage';
import ErrorStage from './Stages/Error/ErrorStage';
import {LoginPopUpStages, setPopUp, getSubscribersData} from '../../FuntoneSlice';
import {
  createOtpLoginStep1,
  createOtpLoginStep02,
  createOtpLoginStep3,
} from './../../../../../utilities/authentication/authenticationSlice';
import {useScreenNavigation} from '../../Utils/ScreenNavigationUtils';

const LoginPopUp = ({funtonePopUp, setPopUp, closePopUp, createOtpLoginStep1, createOtpLoginStep02, createOtpLoginStep3, subscribersData, isSubscribersFetchFailed}) => {
  const [restrictedCodeError, setRestrictedCodeError] = React.useState('');
  const popup = funtonePopUp || {};
  const currentStage = popup.stage || LoginPopUpStages.PHONE;
  const popupData = popup.data;
  const popupError = popup.error;

  const {navigateToSelectSubscriber} = useScreenNavigation();

  // Refs so async callbacks always read the latest prop values (avoids stale closures)
  const subscribersDataRef = useRef(subscribersData);
  useEffect(() => {
    subscribersDataRef.current = subscribersData;
  }, [subscribersData]);

  // When this ref is true, close the popup as soon as subscribersData arrives
  const closeOnSubscribersReadyRef = useRef(false);

  const close = useCallback(() => {
    // Close popup immediately
    try {
      if (closePopUp) closePopUp();
      else setPopUp(null);
    } catch (e) {
      // ignore
    }

    // If there was an intent, navigate after closing
    try {
      const intent = popupData && popupData.intent;
      if (intent && intent.type === 'NAVIGATE_TO_SELECT_SUBSCRIBER') {
        navigateToSelectSubscriber({
          onAfterNavigate: () => {
            // defensive clear
            try {
              if (closePopUp) closePopUp();
              else setPopUp(null);
            } catch (e) {}
            // follow-up actions (e.g. resume download) can be placed here
          },
          noHistorySave: true,
        });
      }
    } catch (err) {
      // ignore
    }
  }, [closePopUp, setPopUp, popupData]);

  // Close the popup as soon as subscribersData arrives (set by OTP success handler)
  useEffect(() => {
    if (closeOnSubscribersReadyRef.current && subscribersData) {
      closeOnSubscribersReadyRef.current = false;
      close();
    }
  }, [subscribersData, close]);

  // Close the popup if the subscribers fetch failed while we were waiting for it
  useEffect(() => {
    if (closeOnSubscribersReadyRef.current && isSubscribersFetchFailed) {
      closeOnSubscribersReadyRef.current = false;
      close();
    }
  }, [isSubscribersFetchFailed, close]);

  const showError = useCallback(
    (phone, message) => {
      setPopUp({type: 'LOGIN', stage: LoginPopUpStages.ERROR, data: phone ? {phone} : undefined, error: message});
    },
    [setPopUp],
  );

  const goToOtpStage = useCallback(
    (extra) => {
      setPopUp({type: 'LOGIN', stage: LoginPopUpStages.OTP, data: {...(popupData || {}), ...(extra || {})}, error: undefined});
    },
    [setPopUp, popupData],
  );

  const handlePhoneSubmit = useCallback(
    async (payload) => {
      const phone = typeof payload === 'string' ? payload : payload && payload.phone;
      if (!phone) return showError(phone, 'Phone number is required');

      try {
        const result = await createOtpLoginStep1({phoneNumber: phone});
        if (result && result.error) return showError(phone, result.error.message || 'Request OTP failed');

        const body = result.payload.Body;
        const ticket = body.message;
        const showRestricted = !!(
          body &&
          (body.restrictedCodeRequired || (body.resultMessage && body.resultMessage.toString().includes('RESTRICT')))
        );
        const showTerms = !!(body && ((body.extra && body.extra.showTerms) || body.termsInd));

        goToOtpStage({phone, ticket, showRestricted, showTerms});
        return result;
      } catch (err) {
        return showError(payload && payload.phone, err && err.message ? err.message : 'Request OTP failed');
      }
    },
    [createOtpLoginStep1, showError, goToOtpStage],
  );

  const handleOtpSubmit = useCallback(
    async (payload) => {
      const code = typeof payload === 'string' ? payload : payload && payload.code;
      const restrictedCode = payload && payload.restrictedCode;
      const phone = popupData && popupData.phone;
      const ticket = popupData && (popupData.ticket || popupData.Guid || popupData.TicketId);

      if (!phone || !ticket) return showError(null, 'Missing phone or ticket. Please retry.');

      try {
        const result = await createOtpLoginStep02({phoneNumber: phone, otpCode: code, guid: ticket, restrictCode: restrictedCode});
        if (result && result.error) {
          // Show error inline in OTP stage instead of navigating to error stage
          setPopUp({type: 'LOGIN', stage: LoginPopUpStages.OTP, data: popupData, error: result.error.message || 'קוד האימות שגוי, נסה שוב'});
          return;
        }

        const body = (result && (result.payload || result.payload?.Body)) || result;
        const isSuccess = !!(body && (body.isSuccess || body.success || body.result === 'SUCCESS'));

        const codeError = (body && (body.codeError || body.errorCode)) || undefined;
        if (codeError === '1003') {
          // move back to phone stage and show the expired-code error in the phone UI
          const message = 'קוד שקיבלתם כבר לא בתוקף. כדי לשמור על הפרטים שלכם, נתחיל את התהליך מחדש';
          setPopUp({type: 'LOGIN', stage: LoginPopUpStages.PHONE, data: {phone}, error: message});
          return;
        }
        if (codeError === "36") {
          setRestrictedCodeError('הקוד שהוזן לא תואם'); 
          return;
        }
        if (codeError === '1005') {
          // Wrong OTP code error - show in OTP stage
          setPopUp({type: 'LOGIN', stage: LoginPopUpStages.OTP, data: popupData, error: 'רגע... זה לא הקוד ששלחנו. שננסה שוב?'});
          return;
        }

        if (isSuccess) {
          if (subscribersDataRef.current) {
            return close();
          }
          closeOnSubscribersReadyRef.current = true;
          return; // keep popup open until data is ready
        }
        
        // If not successful, show error inline in OTP stage
        setPopUp({type: 'LOGIN', stage: LoginPopUpStages.OTP, data: popupData, error: (body && (body.errorMessage || body.resultMessage || body.message)) || 'קוד האימות שגוי, נסה שוב'});
      } catch (err) {
        // Show error inline in OTP stage instead of navigating to error stage
        setPopUp({type: 'LOGIN', stage: LoginPopUpStages.OTP, data: popupData, error: err && err.message ? err.message : 'קוד האימות שגוי, נסה שוב'});
        return;
      }
    },
    [createOtpLoginStep02, createOtpLoginStep3, popupData, close, showError],
  );

  const handleResend = useCallback(async () => {
    const phone = popupData && popupData.phone;
    if (!phone) return Promise.resolve();
    try {
      const result = await createOtpLoginStep1({phoneNumber: phone});
      if (result && result.error) return showError(phone, result.error.message || 'Resend OTP failed');

      const body = (result && (result.payload || result.payload?.Body)) || result;
      const ticket = (body && (body.message || body.ticketId || body.otpTicketId || body.ticket)) || undefined;
      const showRestricted = !!(
        body &&
        (body.restrictedCodeRequired || (body.resultMessage && body.resultMessage.toString().includes('RESTRICT')))
      );
      const showTerms = !!(body && ((body.extra && body.extra.showTerms) || body.termsInd));

      goToOtpStage({phone, ticket, showRestricted, showTerms});
      return result;
    } catch (err) {
      return showError(phone, err && err.message ? err.message : 'Resend OTP failed');
    }
  }, [createOtpLoginStep1, popupData, showError, goToOtpStage]);

  // Step3: ID submission handler — reuse createOtpLoginStep3 logic from main.js
  const handleIdSubmit = useCallback(
    async (payload) => {
      // payload expected to include idNumber and optionally code
      const idNumber = typeof payload === 'string' ? payload : payload && payload.idNumber;
      const code = payload && payload.code ? payload.code : '';
      const phone = popupData && popupData.phone;
      if (!phone) return showError(null, 'Missing phone. Please retry.');

      try {
        const step3 = await createOtpLoginStep3({
          phoneNumber: phone,
          idNumber: idNumber,
          scope: 'PRIVATE_WEBSITE',
          code: code,
        });
        if (step3 && step3.error) return showError(null, step3.error.message || 'ID validation failed');
        const body3 = (step3 && (step3.payload || step3.payload?.Body)) || step3;
        if (body3 && body3.isSuccess) {
          // if server asks for code next, move popup to OTP code substage inside IdValidationPopup by setting data
          if (body3.needsCode) {
            setPopUp({type: 'LOGIN', stage: LoginPopUpStages.ID, data: {...(popupData || {}), code: ''}, error: undefined});
            return;
          }
          // success: close popup and resume any intent
          try {
            if (closePopUp) closePopUp();
            else setPopUp(null);
          } catch (e) {}


          // Close popup - Funtone component will automatically fetch subscriber data

          // resume intent if present
          try {
            const intent = popupData && popupData.intent;
            if (intent && intent.type === 'NAVIGATE_TO_SELECT_SUBSCRIBER') {
              navigateToSelectSubscriber({
                onAfterNavigate: () => {
                  try {
                    if (closePopUp) closePopUp();
                    else setPopUp(null);
                  } catch (e) {}
                },
                noHistorySave: true,
              });
            }
          } catch (err) {}
          return;
        }
        return showError(null, (body3 && (body3.errorMessage || body3.message)) || 'ID validation failed');
      } catch (err) {
        return showError(null, err && err.message ? err.message : 'ID validation failed');
      }
    },
    [createOtpLoginStep3, popupData, setPopUp, closePopUp, navigateToSelectSubscriber, showError],
  );

  const handleRetry = useCallback(
    () => setPopUp({type: 'LOGIN', stage: LoginPopUpStages.PHONE, data: undefined, error: undefined}),
    [setPopUp],
  );

  const stageNode = useMemo(() => {
    switch (currentStage) {
      case LoginPopUpStages.PHONE:
        return <PhoneStage data={popupData} onSubmit={handlePhoneSubmit} />;
      case LoginPopUpStages.OTP:
        return <OtpStage data={popupData} error={popupError} onSubmit={handleOtpSubmit} onResend={handleResend} />;
      case LoginPopUpStages.ID:
        // Render the connected IdValidationPopup. It is already wired to call createOtpLoginStep3
        // but we wrap it so we can intercept close and resume the intent.
        return <IdStage data={popupData} onSubmit={handleIdSubmit} />;
      case LoginPopUpStages.ERROR:
        return <ErrorStage error={popupError} onClose={close} onRetry={handleRetry} />;
      default:
        return <PhoneStage onSubmit={handlePhoneSubmit} />;
    }
  }, [currentStage, popupData, popupError, handlePhoneSubmit, handleOtpSubmit, handleResend, close, handleRetry]);

  return <div className='login-popup'>{stageNode}</div>;
};

const mapStateToProps = (state) => ({
  loginData: state.authentication.loginData,
  funtonePopUp: state.FuntoneSlice && state.FuntoneSlice.popUp,
  subscribersData: state.FuntoneSlice && state.FuntoneSlice.subscribersData,
  isSubscribersFetchFailed: state.FuntoneSlice && state.FuntoneSlice.isSubscribersFetchFailed,
});

const mapDispatchToProps = {
  setPopUp,
  createOtpLoginStep1,
  createOtpLoginStep02,
  createOtpLoginStep3,
  getSubscribersData,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPopUp);
